/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useMemo } from "react";
import chevronLeft from "assets/chevron-left.svg";

const DOTS = "...";

const range = (start, end) => {
  const length = end - start + 1;
  return Array.from({ length }, (_, idx) => idx + start);
};

const usePagination = ({
  totalCount,
  pageSize,
  siblingCount = 1,
  currentPage,
}) => {
  const paginationRange = useMemo(() => {
    const totalPageCount = Math.ceil(totalCount / pageSize);
    const totalPageNumbers = siblingCount + 5;

    // If the number of pages is less than the page numbers we want to show in our paginationComponent, we return the range [0..totalPageCount - 1]
    if (totalPageNumbers >= totalPageCount) {
      return range(0, totalPageCount - 1);
    }

    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
    const rightSiblingIndex = Math.min(
      currentPage + siblingCount,
      totalPageCount,
    );
    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPageCount - 2;

    const firstPageIndex = 0;
    const lastPageIndex = totalPageCount - 1;

    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftItemCount = 3 + 2 * siblingCount;
      const leftRange = range(0, leftItemCount - 1);

      return [...leftRange, DOTS, totalPageCount - 1];
    }

    if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightItemCount = 3 + 2 * siblingCount;
      const rightRange = range(
        totalPageCount - rightItemCount,
        totalPageCount - 1,
      );
      return [firstPageIndex, DOTS, ...rightRange];
    }

    if (shouldShowLeftDots && shouldShowRightDots) {
      const middleRange = range(leftSiblingIndex, rightSiblingIndex);
      return [firstPageIndex, DOTS, ...middleRange, DOTS, lastPageIndex];
    }

    return [];
  }, [totalCount, pageSize, siblingCount, currentPage]);

  return paginationRange;
};

const CustomPagination = ({
  onPageChange,
  totalCount,
  siblingCount = 1,
  currentPage,
  pageSize,
  className,
}) => {
  const paginationRange = usePagination({
    currentPage,
    totalCount,
    siblingCount,
    pageSize,
  });

  if (paginationRange.length < 2) {
    return null;
  }

  const onNext = () => {
    onPageChange(currentPage + 1);
  };

  const onPrevious = () => {
    onPageChange(currentPage - 1);
  };

  const lastPage = paginationRange[paginationRange.length - 1];

  return (
    <ul
      className={`flex border border-neutral-200 rounded divide-solid divide-x-2 ${className}`}
    >
      <li
        style={{ minWidth: 34, minHeight: 34 }}
        onClick={currentPage === 0 ? null : onPrevious}
        className="flex-1 flex items-center justify-center cursor-pointer"
      >
        <img src={chevronLeft} alt="previous page" />
      </li>
      {paginationRange.map((pageNumber) => {
        const isCurrentPage = currentPage === pageNumber;
        const pageNumberStyle = isCurrentPage
          ? "text-black"
          : "text-neutral-500";
        const isDots = pageNumber === DOTS;

        return (
          <li
            className={`${isCurrentPage ? "bg-boost-primary-50" : ""} ${
              isDots ? "cursor-default" : "cursor-pointer"
            }
             flex-1 flex items-center justify-center`}
            onClick={() => (isDots ? null : onPageChange(pageNumber))}
            style={{ minWidth: 34, minHeight: 34 }}
          >
            <p className={`${pageNumberStyle}  text-sm font-medium`}>
              {isDots ? "..." : pageNumber + 1}
            </p>
          </li>
        );
      })}
      <li
        style={{ minWidth: 34, minHeight: 34 }}
        onClick={currentPage === lastPage ? null : onNext}
        className="flex-1 flex items-center justify-center cursor-pointer"
      >
        <img src={chevronLeft} alt="next page" style={{ rotate: "180deg" }} />
      </li>
    </ul>
  );
};

export default CustomPagination;
